import React, { useState, useEffect, useRef, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { EnhancedProductCard } from './EnhancedProductCard';
import { ContactFormModal } from './ContactFormModal';
import SeasonalParticles from './SeasonalParticles';
import ScaryGhostOverlay from './ScaryGhostOverlay';
import HalloweenSceneOverlay from './HalloweenSceneOverlay';
import WinterSceneOverlay from './WinterSceneOverlay';
import ChristmasSceneOverlay from './ChristmasSceneOverlay';
import ValentinesDaySceneOverlay from './ValentinesDaySceneOverlay';
import EasterSceneOverlay from './EasterSceneOverlay';
import ThanksgivingSceneOverlay from './ThanksgivingSceneOverlay';
import FallSceneOverlay from './FallSceneOverlay';
import SummerSceneOverlay from './SummerSceneOverlay';
import { getThemeConfig } from '../themes/seasonalThemes';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Import AppContext from App.js - we'll use it to get user
// We need to export AppContext from App.js first, or pass user as prop

// Particle Animation Component
const ParticleBackground = ({ isDark }) => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    let animationFrameId;
    let particles = [];

    // Set canvas size
    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);

    // Create particles
    class Particle {
      constructor() {
        this.x = Math.random() * canvas.width;
        this.y = Math.random() * canvas.height;
        this.size = Math.random() * 2 + 1;
        this.speedX = (Math.random() - 0.5) * 0.5;
        this.speedY = (Math.random() - 0.5) * 0.5;
        this.opacity = Math.random() * 0.5 + 0.3;
      }

      update() {
        this.x += this.speedX;
        this.y += this.speedY;

        if (this.x > canvas.width) this.x = 0;
        if (this.x < 0) this.x = canvas.width;
        if (this.y > canvas.height) this.y = 0;
        if (this.y < 0) this.y = canvas.height;
      }

      draw() {
        ctx.fillStyle = isDark 
          ? `rgba(167, 139, 250, ${this.opacity})` 
          : `rgba(99, 102, 241, ${this.opacity})`;
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
        ctx.fill();
      }
    }

    // Initialize particles
    const particleCount = Math.min(Math.floor((canvas.width * canvas.height) / 15000), 100);
    for (let i = 0; i < particleCount; i++) {
      particles.push(new Particle());
    }

    // Animation loop
    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      particles.forEach(particle => {
        particle.update();
        particle.draw();
      });

      // Draw connections
      particles.forEach((p1, i) => {
        particles.slice(i + 1).forEach(p2 => {
          const dx = p1.x - p2.x;
          const dy = p1.y - p2.y;
          const distance = Math.sqrt(dx * dx + dy * dy);

          if (distance < 150) {
            ctx.strokeStyle = isDark
              ? `rgba(167, 139, 250, ${0.15 * (1 - distance / 150)})`
              : `rgba(99, 102, 241, ${0.15 * (1 - distance / 150)})`;
            ctx.lineWidth = 0.5;
            ctx.beginPath();
            ctx.moveTo(p1.x, p1.y);
            ctx.lineTo(p2.x, p2.y);
            ctx.stroke();
          }
        });
      });

      animationFrameId = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      window.removeEventListener('resize', resizeCanvas);
      cancelAnimationFrame(animationFrameId);
    };
  }, [isDark]);

  return (
    <canvas
      ref={canvasRef}
      className="absolute inset-0 w-full h-full"
      style={{ pointerEvents: 'none' }}
    />
  );
};

const PremiumHomePage = ({ isDark, toggleTheme, user }) => {
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [settings, setSettings] = useState(null);
  const [showContactModal, setShowContactModal] = useState(false);
  const [showAIAnnouncement, setShowAIAnnouncement] = useState(true);
  const navigate = useNavigate();
  
  const handleCloseAnnouncement = () => {
    setShowAIAnnouncement(false);
    // Note: No localStorage - announcement will show again on page refresh
  };
  
  // Refs for scroll animation
  const productsRef = useRef(null);
  const servicesRef = useRef(null);
  const footerRef = useRef(null);

  // Track visitor on page load
  useEffect(() => {
    const trackVisit = async () => {
      try {
        await axios.post(`${API}/analytics/track-visit`, {
          page: window.location.pathname
        });
      } catch (error) {
        // Silently fail - don't disrupt user experience
        console.debug('Visitor tracking failed:', error);
      }
    };
    trackVisit();
  }, []);

  useEffect(() => {
    loadContent();
    setupScrollAnimations();
  }, []);
  
  const setupScrollAnimations = () => {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -100px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-in');
        }
      });
    }, observerOptions);

    if (productsRef.current) observer.observe(productsRef.current);
    if (servicesRef.current) observer.observe(servicesRef.current);
    if (footerRef.current) observer.observe(footerRef.current);

    return () => observer.disconnect();
  };

  const loadContent = async () => {
    try {
      const [productsRes, settingsRes] = await Promise.all([
        axios.get(`${API}/products?featured=true`),
        axios.get(`${API}/settings/public`)
      ]);

      let featured = productsRes.data.filter(p => p.is_featured || p.featured);
      if (featured.length === 0) {
        featured = productsRes.data.slice(0, 4);
      } else {
        featured = featured.slice(0, 4);
      }
      
      // Debug log to see what images we have
      console.log('Featured products:', featured.map(p => ({
        name: p.name,
        hasImages: !!(p.images && p.images.length > 0),
        hasImage: !!p.image,
        imageData: p.images || p.image
      })));
      
      setFeaturedProducts(featured);
      setSettings(settingsRes.data);
    } catch (error) {
      console.error('Failed to load content:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const getImageUrl = (imagePath) => {
    if (!imagePath) return '';
    if (imagePath.startsWith('http')) return imagePath;
    
    // If path already starts with /api/files/, use it as-is
    if (imagePath.startsWith('/api/files/')) {
      return `${process.env.REACT_APP_BACKEND_URL}${imagePath}`;
    }
    
    // Remove leading slash and 'uploads/' prefix if present
    let cleanPath = imagePath.startsWith('/') ? imagePath.substring(1) : imagePath;
    
    // Handle different path formats
    if (cleanPath.startsWith('api/files/')) {
      // Path already has api/files/, just extract filename
      cleanPath = cleanPath.substring(10); // Remove 'api/files/' prefix
    } else if (cleanPath.startsWith('uploads/')) {
      cleanPath = cleanPath.substring(8); // Remove 'uploads/' prefix
    }
    
    return `${process.env.REACT_APP_BACKEND_URL}/api/files/${cleanPath}`;
  };

  // Icon mapping for service cards
  const getIconSvg = (iconName) => {
    const icons = {
      'printer': 'M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z',
      'lightning': 'M13 10V3L4 14h7v7l9-11h-7z',
      'lightbulb': 'M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z',
      'factory': 'M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10',
      'cube': 'M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4',
      'shield': 'M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z',
      'cog': 'M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z',
      'tools': 'M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z'
    };
    return icons[iconName] || icons['printer'];
  };

  // Get services from settings - all cards are editable via Content Management
  const services = (settings?.service_cards || [])
    .filter(card => card.is_active !== false) // Only show active cards
    .map(card => ({
      title: card.title,
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={getIconSvg(card.icon_name)} />
        </svg>
      ),
      description: card.description,
      bullet_points: card.bullet_points || [],
      order: card.order
    }))
    .sort((a, b) => a.order - b.order);

  // Get theme configuration
  const currentTheme = getThemeConfig(settings?.homepage_theme || 'default');
  const isDarkTheme = ['halloween'].includes(settings?.homepage_theme);

  return (
    <div className={`min-h-screen transition-colors duration-500 ${isDark ? 'bg-gray-900' : 'bg-white'}`} style={{ position: 'relative' }}>
      {/* Winter Scene Overlay - Global Background */}
      {settings && settings.homepage_theme === 'winter' && (
        <WinterSceneOverlay />
      )}
      
      {/* Halloween Scene Overlay - Global Background */}
      {settings && settings.homepage_theme === 'halloween' && (
        <HalloweenSceneOverlay />
      )}
      
      {/* Christmas Scene Overlay - Global Background */}
      {settings && settings.homepage_theme === 'christmas' && (
        <ChristmasSceneOverlay />
      )}
      
      {/* Valentine's Day Scene Overlay - Global Background */}
      {settings && settings.homepage_theme === 'valentines' && (
        <ValentinesDaySceneOverlay />
      )}
      
      {/* Easter Scene Overlay - Global Background */}
      {settings && settings.homepage_theme === 'easter' && (
        <EasterSceneOverlay />
      )}
      
      {/* Thanksgiving Scene Overlay - Global Background */}
      {settings && settings.homepage_theme === 'thanksgiving' && (
        <ThanksgivingSceneOverlay />
      )}
      
      {/* Fall Scene Overlay - Global Background */}
      {settings && settings.homepage_theme === 'fall' && (
        <FallSceneOverlay />
      )}
      
      {/* Summer Scene Overlay - Global Background */}
      {settings && settings.homepage_theme === 'summer' && (
        <SummerSceneOverlay />
      )}
      
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden" style={{ zIndex: 100 }}>
        {/* Gradient Overlay with Seasonal Theme - Removed for Winter/Halloween/Christmas/Valentines/Easter/Thanksgiving/Fall/Summer to show scenes */}
        {settings?.homepage_theme !== 'winter' && settings?.homepage_theme !== 'halloween' && settings?.homepage_theme !== 'christmas' && settings?.homepage_theme !== 'valentines' && settings?.homepage_theme !== 'easter' && settings?.homepage_theme !== 'thanksgiving' && settings?.homepage_theme !== 'fall' && settings?.homepage_theme !== 'summer' && (
          <div className={`absolute inset-0 ${
            isDark 
              ? 'bg-gradient-to-br from-gray-900 via-purple-900/20 to-gray-900' 
              : `bg-gradient-to-br ${currentTheme.gradient}`
          }`} style={{ zIndex: 1 }} />
        )}

        {/* Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-4 py-20 text-center" style={{ zIndex: 200 }}>
          {/* Logo - Main Branding */}
          <div className="mb-12 animate-float">
            <div className={`inline-block p-8 rounded-3xl backdrop-blur-xl shadow-2xl ${
              isDark || isDarkTheme
                ? 'bg-gray-800/95 border-4 border-purple-500/50 shadow-purple-500/30' 
                : 'bg-white/98 border-4 border-blue-200/70 shadow-blue-200/50'
            }`} style={{ 
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(255, 255, 255, 0.1) inset'
            }}>
              {settings?.company_settings?.logo_url && (
                <img
                  src={settings.company_settings.logo_url}
                  alt="RG-3D"
                  className="w-48 h-48 md:w-64 md:h-64 lg:w-80 lg:h-80 mx-auto object-contain filter drop-shadow-2xl"
                  onLoad={(e) => {
                    // Check if image is too small (likely a placeholder)
                  if (e.target.naturalWidth <= 2 && e.target.naturalHeight <= 2) {
                    console.log('Logo appears to be a placeholder (too small)');
                  }
                }}
              />
              )}
            </div>
          </div>
          
          <div className={`inline-block px-10 py-8 rounded-3xl backdrop-blur-xl mb-8 shadow-2xl ${
            isDark || isDarkTheme 
              ? 'bg-gray-900/98 backdrop-blur-xl border-4 border-purple-400/50 shadow-purple-500/40' 
              : 'bg-white/98 backdrop-blur-xl border-4 border-blue-300/60 shadow-blue-300/50'
          }`} style={{ 
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(255, 255, 255, 0.2) inset, 0 -10px 30px -10px rgba(147, 197, 253, 0.3)'
          }}>
            <p className={`text-3xl md:text-5xl font-black mb-6 ${
              settings?.homepage_theme === 'winter' 
                ? 'text-indigo-900' 
                : isDark || isDarkTheme ? 'text-purple-300' : 'text-indigo-700'
            }`} style={{ textShadow: '0 2px 10px rgba(0, 0, 0, 0.1)' }}>
              Premium 3D Creative Studio
            </p>
            
            <p className={`text-xl md:text-2xl max-w-3xl mx-auto leading-relaxed font-medium ${
              settings?.homepage_theme === 'winter'
                ? 'text-gray-900'
                : isDark || isDarkTheme ? 'text-gray-200' : 'text-gray-700'
            }`} style={{ textShadow: '0 1px 3px rgba(0, 0, 0, 0.1)' }}>
              Transforming imagination into reality through cutting-edge additive manufacturing,
              precision engineering, and innovative design solutions
            </p>

            {/* AI Products Announcement */}
            <div className="mt-8 max-w-4xl mx-auto">
              <div className={`relative overflow-hidden rounded-2xl p-8 backdrop-blur-xl ${
                settings?.homepage_theme === 'winter'
                  ? 'bg-white/90 border-2 border-blue-200'
                  : isDark || isDarkTheme 
                  ? 'bg-gradient-to-br from-purple-900/80 to-indigo-900/80 border-2 border-purple-500/30' 
                  : 'bg-gradient-to-br from-purple-50/90 to-indigo-50/90 border-2 border-purple-200'
              } shadow-2xl`}>
                {/* Animated background effect */}
                <div className="absolute inset-0 bg-gradient-to-r from-purple-500/10 to-pink-500/10 animate-pulse" />
                
                {/* Content */}
                <div className="relative z-10">
                  <div className="flex items-center justify-center mb-4">
                    <span className="inline-flex items-center px-4 py-2 rounded-full bg-gradient-to-r from-purple-600 to-pink-600 text-white text-sm font-bold shadow-lg animate-bounce">
                      ✨ NEW: AI-Powered Design Assistant
                    </span>
                  </div>
                  
                  <h3 className={`text-2xl md:text-3xl font-bold mb-4 ${
                    settings?.homepage_theme === 'winter'
                      ? 'text-gray-900'
                      : isDark || isDarkTheme ? 'text-white' : 'text-gray-900'
                  }`}>
                    Co-Create with AI, We Bring It to Life
                  </h3>
                  
                  <p className={`text-base md:text-lg leading-relaxed mb-6 ${
                    settings?.homepage_theme === 'winter'
                      ? 'text-gray-700'
                      : isDark || isDarkTheme ? 'text-gray-200' : 'text-gray-700'
                  }`}>
                    <span className="font-semibold">Imagine having your own personal design assistant!</span> Our revolutionary AI Products feature lets you collaborate with cutting-edge artificial intelligence to create stunning 3D designs. Simply describe your vision, upload your photos, and watch as AI transforms your ideas into professional-grade designs. Once perfected, <span className="font-semibold text-purple-600 dark:text-purple-300">RG3D manufactures your custom creation with precision and care.</span>
                  </p>
                  
                  <div className={`text-center p-4 rounded-lg ${
                    settings?.homepage_theme === 'winter'
                      ? 'bg-yellow-50 border border-yellow-200'
                      : isDark || isDarkTheme ? 'bg-yellow-900/20 border border-yellow-600/30' : 'bg-yellow-50 border border-yellow-200'
                  }`}>
                    <p className={`text-sm font-semibold ${
                      settings?.homepage_theme === 'winter'
                        ? 'text-yellow-800'
                        : isDark || isDarkTheme ? 'text-yellow-300' : 'text-yellow-800'
                    }`}>
                      🔐 Login or sign up to unlock AI Products and start creating your masterpiece today!
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <div className="h-6"></div>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <button
              onClick={() => {
                productsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }}
              className="group relative px-8 py-4 bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-bold rounded-full overflow-hidden shadow-2xl hover:shadow-purple-500/50 transition-all duration-300 transform hover:scale-105"
            >
              <span className="relative z-10">Get Started</span>
              <div className="absolute inset-0 bg-gradient-to-r from-purple-600 to-pink-600 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
            </button>
          </div>

          {/* Scroll Indicator */}
          <div className="absolute bottom-10 left-1/2 transform -translate-x-1/2 animate-bounce">
            <svg className={`w-6 h-6 ${isDark ? 'text-purple-400' : 'text-indigo-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
            </svg>
          </div>
        </div>
      </section>

      {/* Featured Products Section */}
      <section ref={productsRef} className={`py-24 relative scroll-section ${isDark ? 'bg-gray-800/98' : 'bg-white/98'}`} style={{
        boxShadow: '0 -20px 50px -10px rgba(147, 197, 253, 0.2), 0 20px 50px -10px rgba(147, 197, 253, 0.2)',
        backdropFilter: 'blur(20px)',
        zIndex: 100,
        position: 'relative'
      }}>
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-16">
            <div className="inline-block px-8 py-4 rounded-2xl bg-white/90 backdrop-blur-md shadow-lg mb-4">
              <h2 className={`text-5xl md:text-6xl font-black ${
                settings?.homepage_theme === 'winter' 
                  ? 'text-gray-900' 
                  : isDark ? 'text-white' : 'text-gray-900'
              }`} style={{
                textShadow: '0 4px 20px rgba(99, 102, 241, 0.3)'
              }}>
                Featured <span className="bg-clip-text text-transparent bg-gradient-to-r from-indigo-600 to-purple-600">Products</span>
              </h2>
            </div>
            <p className={`text-xl font-medium inline-block px-6 py-2 rounded-lg bg-white/80 backdrop-blur-sm ${
              settings?.homepage_theme === 'winter'
                ? 'text-gray-800'
                : isDark ? 'text-gray-300' : 'text-gray-700'
            }`}>
              Explore our latest innovations and custom creations
            </p>
          </div>

          <div 
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8"
            style={{ 
              animation: `fadeInUp 0.8s ease-out`
            }}
          >
            {featuredProducts.map((product, index) => (
              <div
                key={product.id}
                style={{ 
                  animation: `fadeInUp 0.6s ease-out ${index * 0.1}s both`
                }}
              >
                <EnhancedProductCard product={product} featured={true} />
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <button
              onClick={() => navigate('/products')}
              className="group relative px-10 py-5 bg-gradient-to-r from-indigo-600 to-purple-600 text-white text-lg font-bold rounded-full overflow-hidden shadow-2xl hover:shadow-purple-500/50 transition-all duration-300 transform hover:scale-105"
              style={{ boxShadow: '0 10px 40px -10px rgba(99, 102, 241, 0.5)' }}
            >
              <span className="relative z-10">View All Products</span>
            </button>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section ref={servicesRef} className={`py-20 scroll-section ${isDark ? 'bg-gray-900/98' : 'bg-white/98'}`} style={{
        boxShadow: '0 -20px 50px -10px rgba(147, 197, 253, 0.2), 0 20px 50px -10px rgba(147, 197, 253, 0.2)',
        backdropFilter: 'blur(20px)',
        zIndex: 100,
        position: 'relative'
      }}>
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-16">
            <div className="inline-block px-8 py-4 rounded-2xl bg-white/90 backdrop-blur-md shadow-lg mb-4">
              <h2 className={`text-5xl md:text-6xl font-black ${
                settings?.homepage_theme === 'winter'
                  ? 'text-gray-900'
                  : isDark ? 'text-white' : 'text-gray-900'
              }`} style={{
                textShadow: '0 4px 20px rgba(99, 102, 241, 0.3)'
              }}>
                Our <span className="bg-clip-text text-transparent bg-gradient-to-r from-indigo-600 to-purple-600">Services</span>
              </h2>
            </div>
            <p className={`text-xl font-medium inline-block px-6 py-2 rounded-lg bg-white/80 backdrop-blur-sm ${
              settings?.homepage_theme === 'winter'
                ? 'text-gray-800'
                : isDark ? 'text-gray-300' : 'text-gray-700'
            }`}>
              End-to-end solutions for your creative and manufacturing needs
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {services.map((service, index) => (
              <div
                key={index}
                className={`group p-6 rounded-2xl border-2 transition-all duration-300 hover:scale-105 ${
                  isDark
                    ? 'bg-gray-800/98 backdrop-blur-xl border-gray-600 hover:border-purple-400 hover:shadow-2xl hover:shadow-purple-500/30'
                    : 'bg-white/98 backdrop-blur-xl border-blue-200 hover:border-indigo-400 hover:shadow-2xl hover:shadow-indigo-500/30'
                }`}
                style={{
                  animation: `fadeInUp 0.5s ease-out ${index * 0.08}s both`,
                  boxShadow: isDark 
                    ? '0 10px 30px -5px rgba(139, 92, 246, 0.2)' 
                    : '0 10px 30px -5px rgba(99, 102, 241, 0.2)'
                }}
              >
                <div className="flex items-start gap-4">
                  <div className={`w-12 h-12 flex-shrink-0 rounded-xl flex items-center justify-center transition-all duration-300 ${
                    isDark
                      ? 'bg-purple-900/50 text-purple-400 group-hover:bg-purple-500 group-hover:text-white group-hover:shadow-lg group-hover:shadow-purple-500/50'
                      : 'bg-indigo-100 text-indigo-600 group-hover:bg-indigo-600 group-hover:text-white group-hover:shadow-lg group-hover:shadow-indigo-500/50'
                  }`}>
                    <div className="scale-90">
                      {service.icon}
                    </div>
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="inline-block px-3 py-1.5 rounded-lg bg-white/80 backdrop-blur-sm mb-3">
                      <h3 className={`text-lg font-bold ${
                        settings?.homepage_theme === 'winter'
                          ? 'text-gray-900'
                          : isDark ? 'text-white' : 'text-gray-900'
                      }`}>
                        {service.title}
                      </h3>
                    </div>
                    
                    {/* Description */}
                    {service.description && (
                      <div className="inline-block px-3 py-2 rounded bg-white/70 backdrop-blur-sm mb-3">
                        <p className={`text-sm leading-relaxed ${
                          settings?.homepage_theme === 'winter'
                            ? 'text-gray-800'
                            : isDark ? 'text-gray-300' : 'text-gray-700'
                        }`}>
                          {service.description}
                        </p>
                      </div>
                    )}
                    
                    {/* Bullet Points */}
                    {service.bullet_points && service.bullet_points.length > 0 && (
                      <ul className="space-y-2">
                        {service.bullet_points.map((bullet, idx) => (
                          <li key={idx} className="flex items-start text-sm leading-relaxed">
                            <span className={`${isDark ? 'text-purple-400' : 'text-indigo-500'} mr-2 mt-0.5 flex-shrink-0 font-bold`}>•</span>
                            <span className={`inline-block px-2 py-1 rounded bg-white/60 backdrop-blur-sm ${
                              settings?.homepage_theme === 'winter'
                                ? 'text-gray-800'
                                : isDark ? 'text-gray-300' : 'text-gray-700'
                            }`}>{bullet}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer Section */}
      <footer ref={footerRef} className={`py-20 border-t-4 scroll-section ${isDark ? 'bg-gray-900/98 border-purple-500/30' : 'bg-white/98 border-blue-300/50'}`} style={{
        boxShadow: '0 -20px 50px -10px rgba(147, 197, 253, 0.3)',
        backdropFilter: 'blur(20px)',
        zIndex: 100,
        position: 'relative'
      }}>
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-12">
            {/* About */}
            <div>
              <div className="inline-block px-4 py-2 rounded-lg bg-white/80 backdrop-blur-sm mb-4">
                <h3 className={`text-xl font-black ${
                  settings?.homepage_theme === 'winter'
                    ? 'text-gray-900'
                    : isDark ? 'text-white' : 'text-gray-900'
                }`} style={{
                  textShadow: isDark ? '0 2px 10px rgba(139, 92, 246, 0.3)' : '0 2px 10px rgba(99, 102, 241, 0.2)'
                }}>
                  About RG-3D
                </h3>
              </div>
              <p className={`text-sm font-medium inline-block px-3 py-1.5 rounded bg-white/70 backdrop-blur-sm ${
                settings?.homepage_theme === 'winter'
                  ? 'text-gray-800'
                  : isDark ? 'text-gray-300' : 'text-gray-700'
              }`}>
                Premium 3D Creative Studio specializing in cutting-edge additive manufacturing,
                precision engineering, and innovative design solutions.
              </p>
            </div>

            {/* Quick Links */}
            <div>
              <div className="inline-block px-4 py-2 rounded-lg bg-white/80 backdrop-blur-sm mb-4">
                <h3 className={`text-xl font-black ${
                  settings?.homepage_theme === 'winter'
                    ? 'text-gray-900'
                    : isDark ? 'text-white' : 'text-gray-900'
                }`} style={{
                  textShadow: isDark ? '0 2px 10px rgba(139, 92, 246, 0.3)' : '0 2px 10px rgba(99, 102, 241, 0.2)'
                }}>
                  Quick Links
                </h3>
              </div>
              <ul className="space-y-2">
                {/* Products - Always visible */}
                <li>
                  <button
                    onClick={() => navigate('/products')}
                    className={`text-sm hover:text-purple-500 transition-colors inline-block px-3 py-1.5 rounded bg-white/60 backdrop-blur-sm hover:bg-white/80 ${
                      settings?.homepage_theme === 'winter'
                        ? 'text-gray-800'
                        : isDark ? 'text-gray-400' : 'text-gray-600'
                    }`}
                  >
                    Products
                  </button>
                </li>
                
                {/* My Account - Only for logged-in users */}
                {user && (
                  <li>
                    <button
                      onClick={() => navigate('/account')}
                      className={`text-sm hover:text-purple-500 transition-colors inline-block px-3 py-1.5 rounded bg-white/60 backdrop-blur-sm hover:bg-white/80 ${
                        settings?.homepage_theme === 'winter'
                          ? 'text-gray-800'
                          : isDark ? 'text-gray-400' : 'text-gray-600'
                      }`}
                    >
                      My Account
                    </button>
                  </li>
                )}
                
                {/* Contact - Only for logged-in users */}
                {user && (
                  <li>
                    <button
                      onClick={() => setShowContactModal(true)}
                      className={`text-sm hover:text-purple-500 transition-colors inline-block px-3 py-1.5 rounded bg-white/60 backdrop-blur-sm hover:bg-white/80 ${
                        settings?.homepage_theme === 'winter'
                          ? 'text-gray-800'
                          : isDark ? 'text-gray-400' : 'text-gray-600'
                      }`}
                    >
                      Contact
                    </button>
                  </li>
                )}
              </ul>
            </div>

            {/* Legal Pages - Part 1 */}
            <div>
              <div className="inline-block px-4 py-2 rounded-lg bg-white/80 backdrop-blur-sm mb-4">
                <h3 className={`text-xl font-black ${
                  settings?.homepage_theme === 'winter'
                    ? 'text-gray-900'
                    : isDark ? 'text-white' : 'text-gray-900'
                }`} style={{
                  textShadow: isDark ? '0 2px 10px rgba(139, 92, 246, 0.3)' : '0 2px 10px rgba(99, 102, 241, 0.2)'
                }}>
                  Legal
                </h3>
              </div>
              <ul className="space-y-2">
                <li>
                  <button
                    onClick={() => navigate('/policies/about-us')}
                    className={`text-sm hover:text-purple-500 transition-colors inline-block px-3 py-1.5 rounded bg-white/60 backdrop-blur-sm hover:bg-white/80 ${
                      settings?.homepage_theme === 'winter'
                        ? 'text-gray-800'
                        : isDark ? 'text-gray-400' : 'text-gray-600'
                    }`}
                  >
                    About Us
                  </button>
                </li>
                <li>
                  <button
                    onClick={() => navigate('/policies/terms-and-conditions')}
                    className={`text-sm hover:text-purple-500 transition-colors inline-block px-3 py-1.5 rounded bg-white/60 backdrop-blur-sm hover:bg-white/80 ${
                      settings?.homepage_theme === 'winter'
                        ? 'text-gray-800'
                        : isDark ? 'text-gray-400' : 'text-gray-600'
                    }`}
                  >
                    Terms & Conditions
                  </button>
                </li>
                <li>
                  <button
                    onClick={() => navigate('/policies/privacy-policy')}
                    className={`text-sm hover:text-purple-500 transition-colors inline-block px-3 py-1.5 rounded bg-white/60 backdrop-blur-sm hover:bg-white/80 ${
                      settings?.homepage_theme === 'winter'
                        ? 'text-gray-800'
                        : isDark ? 'text-gray-400' : 'text-gray-600'
                    }`}
                  >
                    Privacy Policy
                  </button>
                </li>
                <li>
                  <button
                    onClick={() => navigate('/policies/refund-return-policy')}
                    className={`text-sm hover:text-purple-500 transition-colors inline-block px-3 py-1.5 rounded bg-white/60 backdrop-blur-sm hover:bg-white/80 ${
                      settings?.homepage_theme === 'winter'
                        ? 'text-gray-800'
                        : isDark ? 'text-gray-400' : 'text-gray-600'
                    }`}
                  >
                    Refund & Returns
                  </button>
                </li>
                <li>
                  <button
                    onClick={() => navigate('/policies/shipping-policy')}
                    className={`text-sm hover:text-purple-500 transition-colors inline-block px-3 py-1.5 rounded bg-white/60 backdrop-blur-sm hover:bg-white/80 ${
                      settings?.homepage_theme === 'winter'
                        ? 'text-gray-800'
                        : isDark ? 'text-gray-400' : 'text-gray-600'
                    }`}
                  >
                    Shipping Policy
                  </button>
                </li>
              </ul>
            </div>

            {/* Legal Pages - Part 2 */}
            <div>
              <div className="inline-block px-4 py-2 rounded-lg bg-white/80 backdrop-blur-sm mb-4">
                <h3 className={`text-xl font-black ${
                  settings?.homepage_theme === 'winter'
                    ? 'text-gray-900'
                    : isDark ? 'text-white' : 'text-gray-900'
                }`} style={{
                  textShadow: isDark ? '0 2px 10px rgba(139, 92, 246, 0.3)' : '0 2px 10px rgba(99, 102, 241, 0.2)'
                }}>
                  Policies
                </h3>
              </div>
              <ul className="space-y-2">
                <li>
                  <button
                    onClick={() => navigate('/policies/payment-deposit-terms')}
                    className={`text-sm hover:text-purple-500 transition-colors inline-block px-3 py-1.5 rounded bg-white/60 backdrop-blur-sm hover:bg-white/80 ${
                      settings?.homepage_theme === 'winter'
                        ? 'text-gray-800'
                        : isDark ? 'text-gray-400' : 'text-gray-600'
                    }`}
                  >
                    Payment & Deposits
                  </button>
                </li>
                <li>
                  <button
                    onClick={() => navigate('/policies/accessibility-statement')}
                    className={`text-sm hover:text-purple-500 transition-colors inline-block px-3 py-1.5 rounded bg-white/60 backdrop-blur-sm hover:bg-white/80 ${
                      settings?.homepage_theme === 'winter'
                        ? 'text-gray-800'
                        : isDark ? 'text-gray-400' : 'text-gray-600'
                    }`}
                  >
                    Accessibility
                  </button>
                </li>
                <li>
                  <button
                    onClick={() => navigate('/policies/disclaimer')}
                    className={`text-sm hover:text-purple-500 transition-colors inline-block px-3 py-1.5 rounded bg-white/60 backdrop-blur-sm hover:bg-white/80 ${
                      settings?.homepage_theme === 'winter'
                        ? 'text-gray-800'
                        : isDark ? 'text-gray-400' : 'text-gray-600'
                    }`}
                  >
                    Disclaimer
                  </button>
                </li>
              </ul>
            </div>

            {/* Social Media */}
            <div>
              <div className="inline-block px-4 py-2 rounded-lg bg-white/80 backdrop-blur-sm mb-4">
                <h3 className={`text-xl font-black ${
                  settings?.homepage_theme === 'winter'
                    ? 'text-gray-900'
                    : isDark ? 'text-white' : 'text-gray-900'
                }`} style={{
                  textShadow: isDark ? '0 2px 10px rgba(139, 92, 246, 0.3)' : '0 2px 10px rgba(99, 102, 241, 0.2)'
                }}>
                  Connect With Us
                </h3>
              </div>
              <div className="flex space-x-4 mb-4">
                {/* Facebook */}
                {settings?.company_settings?.social_media_links?.facebook && (
                  <a
                    href={settings.company_settings.social_media_links.facebook}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300 ${
                      isDark
                        ? 'bg-gray-800 text-gray-400 hover:bg-blue-600 hover:text-white'
                        : 'bg-gray-100 text-gray-600 hover:bg-blue-600 hover:text-white'
                    }`}
                  >
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                    </svg>
                  </a>
                )}

                {/* Twitter */}
                {settings?.company_settings?.social_media_links?.twitter && (
                  <a
                    href={settings.company_settings.social_media_links.twitter}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300 ${
                      isDark
                        ? 'bg-gray-800 text-gray-400 hover:bg-blue-400 hover:text-white'
                        : 'bg-gray-100 text-gray-600 hover:bg-blue-400 hover:text-white'
                    }`}
                  >
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z"/>
                    </svg>
                  </a>
                )}

                {/* LinkedIn */}
                {settings?.company_settings?.social_media_links?.linkedin && (
                  <a
                    href={settings.company_settings.social_media_links.linkedin}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300 ${
                      isDark
                        ? 'bg-gray-800 text-gray-400 hover:bg-blue-700 hover:text-white'
                        : 'bg-gray-100 text-gray-600 hover:bg-blue-700 hover:text-white'
                    }`}
                  >
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                    </svg>
                  </a>
                )}

                {/* Instagram */}
                {settings?.company_settings?.social_media_links?.instagram && (
                  <a
                    href={settings.company_settings.social_media_links.instagram}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300 ${
                      isDark
                        ? 'bg-gradient-to-br from-purple-600 to-pink-600 text-white hover:from-purple-700 hover:to-pink-700'
                        : 'bg-gradient-to-br from-purple-500 to-pink-500 text-white hover:from-purple-600 hover:to-pink-600'
                    }`}
                  >
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12.017 0C8.396 0 7.989.013 7.041.048 6.094.082 5.52.204 5.015.386c-.526.196-1.043.455-1.504.91-.46.455-.719.978-.91 1.504-.181.505-.304 1.079-.338 2.026C2.231 5.773 2.218 6.18 2.218 9.8v4.368c0 3.62.013 4.027.048 4.975.034.947.157 1.521.338 2.026.196.526.455 1.043.91 1.504.455.46.978.719 1.504.91.505.181 1.079.304 2.026.338.948.034 1.355.048 4.975.048h4.037c3.62 0 4.027-.013 4.975-.048.947-.034 1.521-.157 2.026-.338.526-.196 1.043-.455 1.504-.91.46-.455.719-.978.91-1.504.181-.505.304-1.079.338-2.026.034-.948.048-1.355.048-4.975V9.8c0-3.62-.013-4.027-.048-4.975-.034-.947-.157-1.521-.338-2.026a4.118 4.118 0 00-.91-1.504A4.118 4.118 0 0019.49.386C18.985.204 18.411.082 17.464.048 16.516.013 16.109 0 12.489 0h-.472zm-.52 2.169h.545c3.585 0 4.005.012 4.429.048.747.034 1.155.16 1.426.266.359.14.615.307.884.576.27.27.437.525.576.884.107.271.232.679.266 1.426.036.424.048.844.048 4.429v4.037c0 3.585-.012 4.005-.048 4.429-.034.747-.16 1.155-.266 1.426-.14.359-.307.615-.576.884a2.375 2.375 0 01-.884.576c-.271.107-.679.232-1.426.266-.424.036-.844.048-4.429.048h-4.037c-3.585 0-4.005-.012-4.429-.048-.747-.034-1.155-.16-1.426-.266a2.375 2.375 0 01-.884-.576 2.375 2.375 0 01-.576-.884c-.107-.271-.232-.679-.266-1.426-.036-.424-.048-.844-.048-4.429V9.8c0-3.585.012-4.005.048-4.429.034-.747.16-1.155.266-1.426.14-.359.307-.615.576-.884.27-.27.525-.437.884-.576.271-.107.679-.232 1.426-.266.424-.036.844-.048 4.429-.048zm.52 3.83a6 6 0 110 12 6 6 0 010-12zm0 2a4 4 0 100 8 4 4 0 000-8zm7.604-1.604a1.4 1.4 0 11-2.8 0 1.4 1.4 0 012.8 0z"/>
                    </svg>
                  </a>
                )}
              </div>
              <p className={`text-sm inline-block px-3 py-1.5 rounded bg-white/60 backdrop-blur-sm ${
                settings?.homepage_theme === 'winter'
                  ? 'text-gray-800'
                  : isDark ? 'text-gray-400' : 'text-gray-600'
              }`}>
                Follow us for updates and inspiration
              </p>
            </div>
          </div>

          {/* Copyright */}
          <div className={`pt-8 border-t text-center ${isDark ? 'border-gray-800 text-gray-400' : 'border-gray-200 text-gray-600'}`}>
            <p>&copy; {new Date().getFullYear()} RG-3D. All rights reserved.</p>
            <p className="mt-2 text-sm">
              Premium 3D Creative Studio | Precision Engineering & Innovative Design
            </p>
          </div>
        </div>
      </footer>

      {/* CSS Animations */}
      <style jsx>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes float {
          0%, 100% {
            transform: translateY(0px);
          }
          50% {
            transform: translateY(-20px);
          }
        }

        @keyframes slideInFromLeft {
          from {
            opacity: 0;
            transform: translateX(-50px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }

        @keyframes slideInFromRight {
          from {
            opacity: 0;
            transform: translateX(50px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }

        @keyframes scaleIn {
          from {
            opacity: 0;
            transform: scale(0.9);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }

        .animate-float {
          animation: float 6s ease-in-out infinite;
        }

        .line-clamp-2 {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }

        .scroll-section {
          opacity: 0;
          transform: translateY(30px);
          transition: opacity 0.8s ease-out, transform 0.8s ease-out;
        }

        .scroll-section.animate-in {
          opacity: 1;
          transform: translateY(0);
        }

        .scroll-section:nth-child(even).animate-in {
          animation: slideInFromLeft 0.8s ease-out forwards;
        }

        .scroll-section:nth-child(odd).animate-in {
          animation: slideInFromRight 0.8s ease-out forwards;
        }
      `}</style>
      
      {/* Contact Form Modal */}
      <ContactFormModal isOpen={showContactModal} onClose={() => setShowContactModal(false)} />
    </div>
  );
};

export default PremiumHomePage;
